import {Match} from '@wandb/weave/common/types/base';
import {
  FancyPage,
  FancyPageSidebarItem,
} from '@wandb/weave/components/FancyPage';
import {IconName} from '@wandb/weave/components/Icon';
import {JSONNaN} from '@wandb/weave/core';
import {ApolloClient, ApolloQueryResult} from 'apollo-client';
import _, {flowRight as compose} from 'lodash';
import React, {FC, useCallback, useEffect, useMemo} from 'react';
import {graphql, withApollo} from 'react-apollo';
import {connect} from 'react-redux';
import {Redirect} from 'react-router';
import {useParams} from 'react-router-dom';
import {Dispatch} from 'redux';

import AnonymousClaimBanner from '../components/AnonymousClaimBanner';
import ArtifactFiles from '../components/ArtifactFiles';
import {Log} from '../components/Log';
import NoMatch from '../components/NoMatch';
import {useDetectPanelVariant} from '../components/Panel/hooks';
import RunFiles from '../components/RunFiles';
import RunModelInfo from '../components/RunModelInfo';
import RunSystemMetrics from '../components/RunSystemMetrics';
import {envIsLocal, urlPrefixed} from '../config';
import AutoRefreshSettings from '../containers/AutoRefreshSettings';
import * as ViewMutations from '../containers/ViewMutations';
import {useRunArtifactVersionsQuery} from '../generated/graphql';
import {PROJECT_UPSERT} from '../graphql/models';
import {
  RunPageRunQueryResultProps,
  withRunPageRunQuery,
} from '../graphql/runPageRunQuery';
import {DELETE_RUN, RUN_STOP, RUN_UPSERT} from '../graphql/runs';
import {withViewer} from '../graphql/users_get_viewer';
import * as PollingReducer from '../state/polling/reducer';
import {RunPageRunQuery, User} from '../types/graphql';
import {useAdminModeActive} from '../util/admin';
import {setDocumentDescription, setDocumentTitle} from '../util/document';
import history from '../util/history';
import {trackPageViewed} from '../util/navigation';
import {AccessOption} from '../util/permissions';
import * as RunHelpers from '../util/runhelpers';
import * as Run from '../util/runs';
import * as urls from '../util/urls';
import {LoaderProxy} from './../containers/AppLoader';
import NoAccessWrapper from './NoAccessPage/NoAccessWrapper';
import {ProjectPageContextProvider} from './ProjectPage/ProjectPageContext';
import RunArtifacts from './Run/RunArtifacts';
import RunOverview from './Run/RunOverview';
import {RunWorkspaceRunProvider} from './Run/RunWorkspaceRunContext';
import {SingleRunWorkspaceWrapper} from './Run/RunWorkspaceWorkspace';
import {useBasePollInterval} from './Run/useBasePollInterval';

const runFromJson = _.memoize(Run.fromJson);

interface RunMatch extends Match {
  params: {
    entityName: string;
    projectName: string;
    runName: string;
    tab?: string;
    filePath?: string;
  };
}

interface RunPageProps {
  client: ApolloClient<any>;
  match: RunMatch;
  viewer: User;
  autoRefreshEnabled: boolean;
  dispatch: Dispatch<PollingReducer.ActionType>;
  upsertView(
    variables: ViewMutations.ViewUpsertVariables
  ): Promise<ApolloQueryResult<ViewMutations.ViewUpsertResult>>;
  updateProject(vars: {
    id: string;
    entityName: string;
    name: string;
    views?: string;
    access?: string;
  }): Promise<ApolloQueryResult<{}>>;
  updateRun(vars: {
    id: string;
    tags: string[];
    displayName: string;
    notes: string;
  }): Promise<ApolloQueryResult<{}>>;
  deleteRun(id: string, deleteArtifacts?: boolean): void;
}

// TODO: There are a lot of unnecessary non-null assertions (Typescript's
//   ! operator). We can get rid of them by making fewer things optional in
//   ReportPanelGridSection
const RunPage: FC<RunPageProps & RunPageRunQueryResultProps> = React.memo(
  ({
    match,
    viewer,
    autoRefreshEnabled,
    updateProject,
    updateRun,
    deleteRun,
    runQuery,
  }) => {
    const project = runQuery.project;
    const run = project?.run;
    const {panelId} = useParams<{panelId: string}>();
    const isAdminModeActive = useAdminModeActive();
    const {isEmbeddedPanel} = useDetectPanelVariant();

    const isPublicOrOpenProject =
      project?.access === AccessOption.Public ||
      project?.access === AccessOption.Open;

    const pollInterval = useBasePollInterval({
      runState: run?.state ?? undefined,
      runHistoryKeys: run?.historyKeys,
      autoRefreshEnabled,
    });

    const {entityName, projectName, runName, tab, filePath} = match.params;
    const runArtifacts = useRunArtifactVersionsQuery({
      variables: {
        entityName,
        projectName,
        runName,
        includeOutput: true,
        includeInput: false,
      },
    });
    const inputArtifactsCount = run?.inputArtifacts?.totalCount ?? 0;
    const outputArtifactsCount = run?.outputArtifacts?.totalCount ?? 0;
    const hasArtifacts = inputArtifactsCount + outputArtifactsCount > 0;

    const tabDescription: string = useMemo(() => {
      switch (tab) {
        case `overview`:
          return `Overview`;
        case `system`:
          return `System`;
        case `tensorboard`:
          return `Tensorboard`;
        case `model`:
          return `Model`;
        case `models`:
          return `Models`;
        case `logs`:
          return `Logs`;
        case `files`:
          return 'Files';
        case `artifacts`:
          return 'Artifacts';
        case `code`:
          return 'Code';
        case `workspace`:
        default:
          return 'Workspace';
      }
    }, [tab]);

    useEffect(() => {
      if (run == null) {
        return;
      }
      const runDisplayName = run.displayName ?? runName;
      setDocumentTitle(`${runDisplayName} | ${projectName}`);
      setDocumentDescription(
        `${tabDescription} of run ${runDisplayName} in ${projectName}, a machine learning project by ${entityName} using Weights & Biases.`
      );
    }, [tabDescription, run, runName, projectName, entityName]);

    /**
     * A wrapper around `updateRun` (a PUT operation) that allows
     * the caller to specify only the fields to change (i.e.
     * a PATCH operation)
     */
    const patchRun = useCallback(
      fields => {
        if (run) {
          updateRun({
            id: run.id,
            tags: run.tags.map(t => t.name),
            displayName: run.displayName,
            notes: run.notes,
            ...fields,
          });
        }
      },
      [run, updateRun]
    );

    if (runQuery.loading) {
      return <LoaderProxy name="run-query-loading-loader" />;
    } else if (project && project.entity.claimingEntity != null) {
      // If the run's entity has been claimed, redirect to the claiming entity
      // instead so the original link still works.
      return (
        <Redirect
          to={urls.run({
            entityName: project.entity.claimingEntity.name,
            projectName,
            name: runName,
          })}
        />
      );
    } else if (project == null || run == null) {
      return <NoMatch />;
    }
    const {entity} = project;
    const {fileCount} = run;
    const summaryMetrics = Run.parseSummary(run.summaryMetrics, run.name) || {};
    const config = JSONNaN.JSONparseNaN(run.config);
    // 'wandb_version' is an extra thing put in by the CLI that is a misnomer
    // and useless.
    delete config.wandb_version;

    const isProjectReadOnly = runQuery.loading || project.readOnly;

    const pollIntervalMaybeDisabled = autoRefreshEnabled ? pollInterval : 0;

    const runObj = runFromJson(run);

    // for page view tracking
    let originPageName = 'Run';
    if (run.group != null) {
      originPageName = 'Group run';
    } else if (run.sweep != null) {
      originPageName = 'Sweep run';
    }

    return (
      <div className={`run-page ${isEmbeddedPanel ? 'sidebar-hidden' : ''}`}>
        <NoAccessWrapper skipCheck={isPublicOrOpenProject}>
          <ProjectPageContextProvider
            project={{
              id: project.id,
              name: projectName,
              entityName: entityName,
            }}>
            <FancyPage
              baseUrl={urls.run({entityName, projectName, name: runName})}
              activeSlug={tab ?? 'workspace'}
              items={[
                {
                  iconName: 'info',
                  name: 'Overview',
                  slug: 'overview',
                  render: () => (
                    <>
                      <AnonymousClaimBanner
                        entityName={entityName}
                        projectName={projectName}
                        run={runName}
                        user={entity.user}
                        viewer={viewer}
                        createdAt={run.createdAt}
                      />
                      <RunOverview
                        project={project}
                        run={run}
                        summaryMetrics={summaryMetrics}
                        config={config}
                        viewer={viewer}
                        history={history}
                        patchRun={patchRun}
                        updateProject={fields =>
                          updateProject({
                            id: project.id,
                            entityName: project.entityName,
                            name: project.name,
                            ...fields,
                          })
                        }
                        deleteRun={deleteRun}
                      />
                    </>
                  ),
                },
                {
                  iconName: 'dashboard-blackboard',
                  name: 'Workspace',
                  slug: 'workspace',
                  render: () => (
                    <>
                      <AnonymousClaimBanner
                        entityName={entityName}
                        projectName={projectName}
                        run={runName}
                        user={entity.user}
                        viewer={viewer}
                        createdAt={run.createdAt}
                      />
                      <RunWorkspaceRunProvider run={runObj}>
                        <SingleRunWorkspaceWrapper
                          entityName={entityName}
                          projectName={projectName}
                          isProjectReadOnly={isProjectReadOnly}
                        />
                      </RunWorkspaceRunProvider>
                    </>
                  ),
                },
                {
                  iconName: 'system-chip-alt',
                  name: 'System',
                  slug: 'system',
                  render: () => (
                    <>
                      <AnonymousClaimBanner
                        entityName={entityName}
                        projectName={projectName}
                        run={runName}
                        user={entity.user}
                        viewer={viewer}
                        createdAt={run.createdAt}
                      />
                      <RunSystemMetrics
                        entityName={entityName}
                        projectName={projectName}
                        runName={runName}
                        sweepName={run.sweep?.name}
                        group={run.group}
                        pollInterval={pollIntervalMaybeDisabled}
                      />
                    </>
                  ),
                },
                ...(run.servicesAvailable.tensorboard && !envIsLocal
                  ? [
                      {
                        iconName: 'tensorflow-logo',
                        name: 'Tensor.', // Truncation per design
                        nameTooltip: 'TensorBoard',
                        slug: 'tensorboard',
                        render: () => (
                          <iframe
                            title="TensorBoard"
                            className="tensorboard-iframe"
                            src={urls.tensorboardUrl(
                              entityName,
                              projectName,
                              runName
                            )}
                          />
                        ),
                      } as FancyPageSidebarItem,
                    ]
                  : []),
                ...(Object.values(summaryMetrics).find(
                  (val: any) =>
                    val && (val._type === 'graph' || val._type === 'graph-file')
                )
                  ? [
                      {
                        iconName: 'model',
                        name: 'Model',
                        slug: 'model',
                        render: () => (
                          <>
                            <AnonymousClaimBanner
                              entityName={entityName}
                              projectName={projectName}
                              run={runName}
                              user={entity.user}
                              viewer={viewer}
                              createdAt={run.createdAt}
                            />
                            <RunModelInfo
                              entityName={entityName}
                              projectName={projectName}
                              runName={runName}
                              sweepName={run.sweep?.name}
                              group={run.group}
                              summaryMetrics={summaryMetrics}
                            />
                          </>
                        ),
                      } as FancyPageSidebarItem,
                    ]
                  : []),
                {
                  iconName: 'list' as IconName,
                  name: 'Logs',
                  slug: 'logs',
                  render: () => (
                    <RunPageLogs
                      entityName={entityName}
                      projectName={projectName}
                      runName={runName}
                      originPageName={originPageName}
                      pollInterval={pollIntervalMaybeDisabled}
                      user={entity.user}
                      viewer={viewer}
                      run={run}
                    />
                  ),
                },
                {
                  iconName: 'document',
                  name: 'Files',
                  slug: 'files',
                  render: () => (
                    <>
                      <AnonymousClaimBanner
                        entityName={entityName}
                        projectName={projectName}
                        run={runName}
                        user={entity.user}
                        viewer={viewer}
                        createdAt={run.createdAt}
                      />
                      <RunFiles
                        entityName={entityName}
                        projectName={projectName}
                        runName={runName}
                        path={filePath}
                        totalFiles={fileCount}
                        history={history}
                        groupName={run.group}
                        sweepName={run.sweep?.name}
                      />
                    </>
                  ),
                },
                ...(hasArtifacts
                  ? [
                      {
                        iconName: 'versions-layers',
                        name: `Artifacts`,
                        slug: 'artifacts',
                        render: () => (
                          <>
                            <AnonymousClaimBanner
                              entityName={entityName}
                              projectName={projectName}
                              run={runName}
                              user={entity.user}
                              viewer={viewer}
                              createdAt={run.createdAt}
                            />
                            <RunArtifacts
                              entityName={entityName}
                              projectName={projectName}
                              runName={runName}
                              groupName={run.group}
                              sweepName={run.sweep?.name}
                            />
                          </>
                        ),
                      } as FancyPageSidebarItem,
                    ]
                  : []),
                ...(config._wandb?.value?.code_path ||
                config._wandb?.value?.session_history
                  ? [
                      {
                        iconName: 'code-alt',
                        name: 'Code',
                        slug: 'code',
                        render: () => {
                          const outputs =
                            runArtifacts.data?.project?.run?.outputArtifacts?.edges.filter(
                              edge => edge?.node?.artifactType.name === 'code'
                            );
                          if (outputs && outputs.length > 0) {
                            const output = outputs[outputs.length - 1];
                            return (
                              <div style={{padding: '0 16px'}}>
                                <ArtifactFiles
                                  entityName={entityName}
                                  projectName={projectName}
                                  artifactTypeName={'code'}
                                  artifactCollectionName={
                                    output.node!.artifactSequence.name
                                  }
                                  artifactCommitHash={output.node!.commitHash!}
                                  filePath={filePath}
                                  setFilePath={base => path =>
                                    history.push(
                                      urls.runCodeFile(
                                        Object.assign({runName, path}, base)
                                      )
                                    )}
                                />
                              </div>
                            );
                          } else {
                            return (
                              <RunFiles
                                entityName={entityName}
                                projectName={projectName}
                                runName={runName}
                                path={
                                  config._wandb.value.code_path ||
                                  config._wandb.value.session_history
                                }
                                totalFiles={fileCount}
                                history={history}
                                groupName={run.group}
                                sweepName={run.sweep?.name}
                              />
                            );
                          }
                        },
                      } as FancyPageSidebarItem,
                    ]
                  : []),
              ]}
            />
          </ProjectPageContextProvider>
        </NoAccessWrapper>
      </div>
    );
  }
);

const withMutations = compose(
  ViewMutations.upsertViewMutation,
  graphql(DELETE_RUN, {
    props: ({mutate, ownProps}: any) => ({
      deleteRun: (id: string, deleteArtifacts?: boolean) => {
        const matchParams = ownProps.match.params;
        if (mutate) {
          mutate({
            variables: {id, deleteArtifacts},
          }).then(
            () =>
              (window.location.href = urlPrefixed(
                `/${matchParams.entityName}/${matchParams.projectName}`
              ))
          );
        }
      },
    }),
  }),
  graphql(RUN_STOP, {
    props: ({mutate}) => ({
      stop: (id: string) =>
        mutate &&
        mutate({
          variables: {id},
        }).then(() => console.log('Stopping run')),
    }),
  }),
  graphql(RUN_UPSERT, {
    props: ({mutate}) => ({
      updateRun: (variables: any) =>
        mutate &&
        mutate({
          variables,
        }),
    }),
  }),
  graphql(PROJECT_UPSERT, {
    props: ({mutate}) => ({
      updateProject: (variables: any) =>
        mutate &&
        mutate({
          variables: {...variables},
        }),
    }),
  })
) as any;

const RunPageWrapped = withViewer(
  // TODO(debt): remove these, replace with hooks. use newer run query style, too.
  withMutations(withApollo(withRunPageRunQuery(connect()(RunPage)) as any))
);

interface PollingRunPageProps {
  match: RunMatch;
}

const PollingRunPage: FC<PollingRunPageProps> = React.memo(props => {
  return (
    <AutoRefreshSettings>
      {autoRefreshEnabled => (
        <RunPageWrapped {...props} autoRefreshEnabled={autoRefreshEnabled} />
      )}
    </AutoRefreshSettings>
  );
});

type RunPageLogsProps = {
  entityName: string;
  projectName: string;
  runName: string;
  originPageName: string;
  pollInterval: number;
  user: User;
  viewer: User;
  run: RunPageRunQuery;
};

const RunPageLogs: React.FC<RunPageLogsProps> = React.memo(
  ({
    entityName,
    projectName,
    runName,
    originPageName,
    pollInterval,
    user,
    viewer,
    run,
  }) => {
    // for page view tracking
    useEffect(() => {
      trackPageViewed(originPageName, 'Logs');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const outputLogFile = RunHelpers.outputLogFile(run);
    return (
      <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <AnonymousClaimBanner
          entityName={entityName}
          projectName={projectName}
          run={runName}
          user={user}
          viewer={viewer}
          createdAt={run.createdAt}
        />
        <div style={{flex: '1 1 auto'}}>
          <Log
            entityName={entityName}
            projectName={projectName}
            runName={runName}
            pollInterval={pollInterval}
            totalLines={run.logLineCount}
            outputLogFile={outputLogFile}
            showTimestamp
            analyticsLocation="run-logs"
          />
        </div>
      </div>
    );
  }
);
RunPageLogs.displayName = 'RunPageLogs';

export default PollingRunPage;
